import useAccountManagementStore, {
    userDataType,
} from "../../../stores/accountManagementStore";
import {
    signInWithPassword,
    updateUserInfo,
} from "../../../utility/backend/authentication";
import { signOut as signOutFrontend } from "../../../utility/backend/authentication";
import { createAccount } from "../../../utility/backend/authentication";
import { resetPassword as resetPasswordBackend } from "../../../utility/backend/authentication";
import { signInWithTokens as signInWithTokensBackend } from "../../../utility/backend/authentication";
import { signInWithGoogle as signInWithGoogleBackend } from "../../../utility/backend/authentication";
import { storeUserAccount } from "../../../utility/cache/storeUserAccount";

export function useAccountManagementFunctions() {
    const accountManagementStore = useAccountManagementStore();

    async function signIn(
        email: string,
        password: string,
    ): Promise<userDataType | null> {
        accountManagementStore.setIsLoading(true);

        try {
            const userData = await signInWithPassword(email, password);

            if (!userData) return null;

            accountManagementStore.setLoginState("default");
            storeUserAccount(userData);
            return userData;
        } finally {
            accountManagementStore.setIsLoading(false);
        }
    }

    /**
     * Sign out the user.
     * Clears the user data from the store and sets the login state to "default".
     * @returns {Promise<void>} - A promise that resolves when the sign out is successful.
     */
    async function signOut() {
        const success = await signOutFrontend();

        if (success) {
            accountManagementStore.setUserData(null);
            accountManagementStore.setLoginState("default");
            storeUserAccount(null);
        }
    }

    async function registerAccount(
        email: string,
        password: string,
    ): Promise<boolean> {
        const result = await createAccount(email, password);

        const userDataExists = result !== null;

        if (userDataExists) return userDataExists;

        storeUserAccount(result);
        return userDataExists;
    }

    async function verifyAccount(
        email: string,
        password: string,
    ): Promise<userDataType | null> {
        const userData = await signInWithPassword(email, password);

        if (!userData) return null;

        storeUserAccount(userData);
        return userData;
    }

    async function updatePassword(
        password: string,
    ): Promise<"same_password" | userDataType | null> {
        const result = await updateUserInfo({ password: password });

        if (isUserDataType(result)) {
            storeUserAccount(result);
        }

        return result;
    }

    async function updateEmail(
        email: string,
        overrideTokens?: { accessToken: string; refreshToken: string },
    ): Promise<"same_password" | userDataType | null> {
        const result = await updateUserInfo({ email: email });

        if (isUserDataType(result)) {
            storeUserAccount(result);
        }

        return result;
    }

    async function resetPassword(email: string): Promise<void> {
        await resetPasswordBackend(email);
    }

    async function signInWithGoogle(directTo?: string) {
        signInWithGoogleBackend(directTo);
    }

    async function signInWithTokens(
        access_token: string,
        refresh_token: string,
    ): Promise<userDataType | null> {
        const userData = await signInWithTokensBackend(
            access_token,
            refresh_token,
        );

        if (!userData) return null;

        storeUserAccount(userData);
        accountManagementStore.setLoginState("default");
        return userData;
    }

    return {
        signIn,
        signOut,
        registerAccount,
        verifyAccount,
        updatePassword,
        updateEmail,
        resetPassword,
        signInWithGoogle,
        signInWithTokens,
    };
}

function isUserDataType(data: unknown): data is userDataType {
    return (
        typeof data === "object" &&
        data !== null &&
        "id" in data &&
        typeof (data as any).id === "string"
    );
}
